import { EmojiEmotions } from '@mui/icons-material';
import { Box, Button, ButtonGroup, Card, IconButton, Tooltip } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import type { Message } from '../../models/message';
import { toggleMessageReaction } from '../../services/reaction.service';

interface EmojiActionProps {
  message: Message;
}

export const EMOJI_REACTIONS = [
  { emoji: '❤️', label: 'Heart', id: '\u2764\uFE0F' },
  { emoji: '😆', label: 'Grinning', id: '\uD83D\uDE06' },
  { emoji: '😮', label: 'Open Mouth', id: '\uD83D\uDE2E' },
  { emoji: '😢', label: 'Crying', id: '\uD83D\uDE22' },
  { emoji: '😡', label: 'Pouting', id: '\uD83D\uDE21' },
  { emoji: '👍', label: 'Thumbs Up', id: '\uD83D\uDC4D' },
  { emoji: '✅', label: 'Check Mark', id: '\u2705' },
  { emoji: '❌', label: 'Cross Mark', id: '\u274C' }
];

export const EmojiAction: React.FC<EmojiActionProps> = ({ message }) => {
  const [open, setOpen] = useState(false);
  const [animatingEmoji, setAnimatingEmoji] = useState<string | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const handleEmojiClick = async (emojiId: string) => {
    setAnimatingEmoji(emojiId);
    await toggleMessageReaction(message.conversationId, message.messageId, {
      bucketId: message.bucketId,
      reaction: emojiId
    });

    // Reset animation after it completes
    setTimeout(() => {
      setAnimatingEmoji(null);
      setOpen(false);
    }, 700);
  };

  return (
    <Tooltip
      open={open}
      ref={anchorRef}
      itemRef=''
      enterDelay={0}
      enterNextDelay={0}
      title={
        <Card
          className='mex:!p-0 mex:!rounded-2xl'
          ref={tooltipRef}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <ButtonGroup variant='text'>
            {EMOJI_REACTIONS.map((emoji) => (
              <Button
                className='mex:!border-none mex:!text-[20px]'
                sx={{
                  minWidth: { xs: 44, md: 32 },
                  minHeight: { xs: 44, md: 32 },
                  padding: { xs: '8px', md: '4px' }
                }}
                key={emoji.id}
                onClick={() => handleEmojiClick(emoji.id)}
                title={emoji.label}
              >
                <Box
                  component='span'
                  sx={{
                    animation: animatingEmoji === emoji.id ? 'bounce 0.6s ease-in-out' : 'none',
                    '@keyframes bounce': {
                      '0%': { transform: 'scale(1)' },
                      '50%': { transform: 'scale(1.3)' },
                      '100%': { transform: 'scale(1)' }
                    }
                  }}
                >
                  {emoji.emoji}
                </Box>
              </Button>
            ))}
          </ButtonGroup>
        </Card>
      }
      placement='auto'
      slotProps={{
        tooltip: {
          sx: {
            padding: 0,
            border: 'none',
            bgcolor: 'transparent',
            boxShadow: 'none'
          }
        }
      }}
    >
      <IconButton
        size='small'
        onClick={() => setOpen(true)}
        sx={{
          width: { xs: 44, md: 28 },
          height: { xs: 44, md: 28 },
          minWidth: { xs: 44, md: 28 },
          minHeight: { xs: 44, md: 28 },
          backgroundColor: 'background.paper',
          color: 'text.primary',
          boxShadow: 1,
          '&:hover': {
            backgroundColor: 'action.hover',
            boxShadow: 2
          },
          '&:disabled': {
            color: 'text.disabled',
            backgroundColor: 'transparent'
          }
        }}
      >
        <EmojiEmotions sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  );
};
